'use client';

import { useState, type ReactNode } from 'react';
import Link from 'next/link';
import { PayloadImage } from '@/components/PayloadImage';
import { RichText } from '@payloadcms/richtext-lexical/react';
import { resolveImageFrame, type ImageFrame } from '@/lib/image-frame';

/**
 * Decorative frames drawn behind the image. Purely visual (aria-hidden); which
 * one renders is decided by resolveImageFrame from the block's `imageFrame`
 * field — `none` by default. Host elements only, so this module-level map is
 * safe to reuse across renders.
 */
const FRAMES: Record<ImageFrame, ReactNode> = {
  /* Tilted gradient backdrop */
  backdrop: (
    <div
      aria-hidden="true"
      className="absolute -inset-3 rounded-3xl"
      style={{
        background: 'linear-gradient(135deg, color-mix(in srgb, var(--color-t-primary) 14%, transparent), color-mix(in srgb, var(--color-t-primary-light, var(--color-t-primary)) 6%, transparent))',
        transform: 'rotate(-2deg)',
      }}
    />
  ),
  /* Corner accent dots */
  dots: (
    <>
      <div
        aria-hidden="true"
        className="absolute -top-4 -left-4 w-24 h-24"
        style={{
          backgroundImage: 'radial-gradient(color-mix(in srgb, var(--color-t-primary) 20%, transparent) 2px, transparent 2px)',
          backgroundSize: '8px 8px',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-4 -right-4 w-24 h-24"
        style={{
          backgroundImage: 'radial-gradient(color-mix(in srgb, var(--color-t-primary) 20%, transparent) 2px, transparent 2px)',
          backgroundSize: '8px 8px',
        }}
      />
    </>
  ),
  /* Double-border offset */
  border: (
    <div
      aria-hidden="true"
      className="absolute -inset-2 rounded-2xl"
      style={{
        border: '2px solid color-mix(in srgb, var(--color-t-primary) 12%, transparent)',
        transform: 'translate(8px, 8px)',
      }}
    />
  ),
  /* Side accent bar + subtle shadow pad */
  bar: (
    <>
      <div
        aria-hidden="true"
        className="absolute top-4 bottom-4 -left-2 w-1 rounded-full"
        style={{
          background: 'linear-gradient(to bottom, var(--color-t-primary), color-mix(in srgb, var(--color-t-primary-light, var(--color-t-primary)) 40%, transparent))',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -inset-2 rounded-3xl"
        style={{
          background: 'color-mix(in srgb, var(--color-t-primary) 4%, transparent)',
        }}
      />
    </>
  ),
};

function ImagePlaceholder() {
  return (
    <div className="w-full aspect-[4/3] rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'color-mix(in srgb, var(--color-t-primary) 6%, transparent)' }}>
      <svg className="w-20 h-20 opacity-20" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24" style={{ color: 'var(--color-t-primary)' }}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
      </svg>
    </div>
  );
}

export function ImageTextBlockRenderer({ block }: { block: any }) {
  const imgLeft = block.imagePosition === 'left';
  const variant = block.variant || 'standard';
  const frame = resolveImageFrame(block.imageFrame, block._imageTextIndex);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const isOdd = ((block._sectionIndex ?? 0) % 2) === 1;

  // Check if any checkmark has its own image
  const hasCheckmarkImages = variant === 'withChecks' && block.checkmarks?.some((c: any) => c.image?.url);

  // Determine which image to display
  let displayImage = block.image;
  if (hasCheckmarkImages && activeIdx !== null && block.checkmarks[activeIdx]?.image?.url) {
    displayImage = block.checkmarks[activeIdx].image;
  }

  return (
    <section
      className="py-16 lg:py-24 relative overflow-hidden"
      style={{
        backgroundColor: isOdd
          ? 'var(--color-t-section-alt)'
          : 'var(--color-t-section-light)',
      }}
    >
      {/* Subtle dot pattern overlay */}
      {isOdd && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(color-mix(in srgb, var(--color-t-primary) 6%, transparent) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
          }}
        />
      )}
      {/* Subtle grid pattern for even sections */}
      {!isOdd && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(color-mix(in srgb, var(--color-t-primary) 3%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in srgb, var(--color-t-primary) 3%, transparent) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image side */}
          <div className={imgLeft ? 'lg:order-1' : 'lg:order-2'}>
            <div className="relative" data-image-frame={frame ?? 'none'}>
              {frame && FRAMES[frame]}
              <div
                className="relative rounded-2xl overflow-hidden"
                style={{
                  border: '1px solid color-mix(in srgb, var(--color-t-primary) 10%, transparent)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                {displayImage?.url ? (
                  <PayloadImage
                    media={displayImage}
                    alt={displayImage.alt || block.heading || ''}
                    targetWidth={1400}
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    className="w-full h-auto block transition-all duration-300"
                  />
                ) : (
                  <ImagePlaceholder />
                )}
              </div>
              {/* Floating badge (withBadge variant) */}
              {variant === 'withBadge' && block.badge?.value && (
                <div className="absolute -bottom-4 -right-4 lg:-right-6 p-4 rounded-xl shadow-lg bg-white border" style={{ borderColor: 'color-mix(in srgb, var(--color-t-primary) 10%, transparent)' }}>
                  <div className="text-2xl font-extrabold" style={{ color: 'var(--color-t-primary)' }}>{block.badge.value}</div>
                  <div className="text-xs" style={{ color: 'var(--color-t-body)' }}>{block.badge.label}</div>
                </div>
              )}
            </div>
          </div>

          {/* Text side */}
          <div className={imgLeft ? 'lg:order-2' : 'lg:order-1'}>
            {block.sectionTag && (
              <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4" style={{ backgroundColor: 'color-mix(in srgb, var(--color-t-primary) 8%, transparent)', color: 'var(--color-t-primary)' }}>
                {block.sectionTag}
              </span>
            )}
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-5 leading-tight" style={{ color: 'var(--color-t-heading)' }}>
              {block.heading}
            </h2>
            {block.body && (
              <div className="prose max-w-none mb-5" style={{ '--tw-prose-headings': 'var(--color-t-heading)', '--tw-prose-body': 'var(--color-t-body)', '--tw-prose-links': 'var(--color-t-cta)' } as React.CSSProperties}>
                <RichText data={block.body} />
              </div>
            )}
            {/* Checkmarks (withChecks variant) */}
            {variant === 'withChecks' && block.checkmarks && block.checkmarks.length > 0 && (
              <ul className="space-y-2 mb-6">
                {block.checkmarks.map((c: any, i: number) => {
                  const isActive = activeIdx === i;
                  const title = c.title || c.text || '';
                  return (
                    <li
                      key={i}
                      className={`flex items-start gap-3 rounded-xl transition-all duration-200 ${
                        hasCheckmarkImages ? 'cursor-pointer p-3 -mx-3' : ''
                      } ${isActive && hasCheckmarkImages ? 'shadow-sm' : ''}`}
                      style={isActive && hasCheckmarkImages ? { backgroundColor: 'color-mix(in srgb, var(--color-t-primary) 6%, transparent)' } : undefined}
                      onMouseEnter={hasCheckmarkImages ? () => setActiveIdx(i) : undefined}
                      onMouseLeave={hasCheckmarkImages ? () => setActiveIdx(null) : undefined}
                    >
                      <span
                        className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors duration-200"
                        style={{ backgroundColor: isActive ? 'var(--color-t-primary)' : 'color-mix(in srgb, var(--color-t-primary) 10%, transparent)' }}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" style={{ color: isActive ? 'white' : 'var(--color-t-primary)' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <div>
                        <span className="font-bold" style={{ color: 'var(--color-t-heading)' }}>{title}</span>
                        {c.description && (
                          typeof c.description === 'string'
                            ? <p className="text-sm mt-1 leading-relaxed" style={{ color: 'var(--color-t-body)' }}>{c.description}</p>
                            : <div className="text-sm mt-1 leading-relaxed" style={{ color: 'var(--color-t-body)' }}><RichText data={c.description} /></div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {block.ctaLabel && block.ctaLink && (
              <Link href={block.ctaLink} className="inline-flex items-center gap-2 font-semibold border-b-2 pb-0.5 hover:opacity-80 transition-opacity" style={{ color: 'var(--color-t-heading)', borderColor: 'var(--color-t-accent, var(--color-t-primary))' }}>
                {block.ctaLabel}
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
